/**
 * @file Confirm Modal Component
 * @description Mobile-friendly confirmation dialog to replace window.confirm()
 */

import React, { useState, useCallback } from 'react';
import '../styles/ConfirmModal.css';

/**
 * useConfirmModal Hook
 *
 * @hook
 * @description Custom hook to manage confirmation modal state.
 * Provides async confirmation with promise-based API.
 *
 * @returns {Object} Modal state and control methods
 * @returns {boolean} return.isOpen - Whether modal is currently visible
 * @returns {string} return.message - Current message to display
 * @returns {string} return.confirmText - Text for confirm button
 * @returns {string} return.cancelText - Text for cancel button
 * @returns {Function} return.confirm - Function to show confirmation dialog
 * @returns {Function} return.close - Function to close modal
 *
 * @example
 * const confirmModal = useConfirmModal();
 *
 * // Show confirmation
 * const confirmed = await confirmModal.confirm('Delete this form?');
 * if (confirmed) {
 *   // User clicked confirm
 * }
 *
 * // Custom button text
 * const confirmed = await confirmModal.confirm(
 *   'Approve 5 forms?',
 *   'Yes, Approve',
 *   'Cancel'
 * );
 */
export const useConfirmModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [confirmText, setConfirmText] = useState('Confirm');
  const [cancelText, setCancelText] = useState('Cancel');
  const [resolver, setResolver] = useState(null);

  const confirm = useCallback((msg, confirmBtn = 'Confirm', cancelBtn = 'Cancel') => {
    setMessage(msg);
    setConfirmText(confirmBtn);
    setCancelText(cancelBtn);
    setIsOpen(true);

    // Return a promise that resolves when user makes choice
    return new Promise((resolve) => {
      setResolver(() => resolve);
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    if (resolver) {
      resolver(true);
      setResolver(null);
    }
  }, [resolver]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    if (resolver) {
      resolver(false);
      setResolver(null);
    }
  }, [resolver]);

  return {
    isOpen,
    message,
    confirmText,
    cancelText,
    confirm,
    handleConfirm,
    handleCancel
  };
};

/**
 * ConfirmModal Component
 *
 * @component
 * @description Mobile-friendly confirmation dialog with large tap targets,
 * smooth animations, and customizable button text.
 *
 * Features:
 * - Large, touch-friendly buttons
 * - Backdrop overlay (click to cancel)
 * - Smooth fade-in animation
 * - Customizable button text
 * - Color-coded buttons (green = confirm, gray = cancel)
 * - Accessible keyboard support
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {string} props.message - Message to display
 * @param {string} [props.confirmText='Confirm'] - Confirm button text
 * @param {string} [props.cancelText='Cancel'] - Cancel button text
 * @param {Function} props.onConfirm - Callback when confirmed
 * @param {Function} props.onCancel - Callback when cancelled
 *
 * @returns {JSX.Element|null} Confirmation modal or null if not open
 *
 * @example
 * <ConfirmModal
 *   isOpen={showConfirm}
 *   message="Are you sure you want to delete this form?"
 *   confirmText="Yes, Delete"
 *   cancelText="Cancel"
 *   onConfirm={() => deleteForm()}
 *   onCancel={() => setShowConfirm(false)}
 * />
 */
export const ConfirmModal = ({
  isOpen,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel
}) => {
  if (!isOpen) return null;

  return (
    <div className="confirm-modal-overlay" onClick={onCancel}>
      <div
        className="confirm-modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="confirm-modal-icon">?</div>
        <div className="confirm-modal-message">{message}</div>
        <div className="confirm-modal-buttons">
          <button
            className="confirm-modal-btn confirm-modal-btn-cancel"
            onClick={onCancel}
          >
            {cancelText}
          </button>
          <button
            className="confirm-modal-btn confirm-modal-btn-confirm"
            onClick={onConfirm}
            autoFocus
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
